import React from "react";
import { useTranslations } from "use-intl";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from "@/components/ui/resizable";
import DocumentViewerPanel from "@/components/shared/DocumentViewerPanel";
import { Receipt } from "@/types/receipt";
import SekEquivalent from "@/components/shared/SekEquivalent";
import { usePaymentVerificationDisplay } from "@/i18n/usePaymentVerificationDisplay";
import { LaterMatchFlag } from "@/components/shared/PaymentVerificationBadge";
import { useFormatters } from "@financialharmony/ui";
import { useReceiptLabels } from "./useReceiptLabels";

interface ReceiptDetailDialogProps {
  receipt: Receipt | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onMatch: (receipt: Receipt) => void;
  formatCurrency: (amount: number, currency: string) => string;
}

const STATUS_BADGE_CLASSNAME: Record<string, string> = {
  matched: "bg-green-50 text-green-700 border-green-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
  unmatched: "bg-amber-50 text-amber-700 border-amber-200",
};

const ReceiptDetailDialog: React.FC<ReceiptDetailDialogProps> = ({
  receipt,
  open,
  onOpenChange,
  onMatch,
  formatCurrency,
}) => {
  const t = useTranslations("consultantBooks.receipts");
  const labels = useReceiptLabels();
  const { formatDate: formatDateValue } = useFormatters();
  const getVerificationDisplay = usePaymentVerificationDisplay();
  if (!receipt) return null;

  const formatDate = (dateStr?: string | null) =>
    dateStr && dateStr.trim() ? formatDateValue(dateStr, "numeric") : "—";

  // #498 Task 6 — design spec AC11: distinct from a receipt that was auto-matched, since
  // receipt.status='matched' alone cannot tell a real bank match apart from an
  // unmatched-account acknowledgment. Absent entirely when no auto_match_decisions row exists
  // for this receipt (e.g. an unmatched/rejected receipt, or one predating this feature).
  const verificationDisplay = getVerificationDisplay(receipt.paymentVerification);

  const detailPanel = (
    <div className="h-full overflow-y-auto space-y-6 pb-4">
      <div className="grid grid-cols-2 gap-4 p-4 bg-muted rounded-lg">
        <div>
          <p className="text-xs text-muted-foreground">{t("detail.labels.vendor")}</p>
          <p className="font-medium">{receipt.vendor || "—"}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">{t("detail.labels.amount")}</p>
          <p className="font-medium">{formatCurrency(receipt.amount, receipt.currency)}</p>
          <SekEquivalent sekAmount={receipt.sek_amount} currency={receipt.currency} />
        </div>
        <div>
          <p className="text-xs text-muted-foreground">{t("detail.labels.date")}</p>
          <p className="font-medium">{formatDate(receipt.transaction_date ?? receipt.due_date ?? receipt.created_at)}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">{t("detail.labels.status")}</p>
          <Badge
            variant="outline"
            className={STATUS_BADGE_CLASSNAME[receipt.status]}
          >
            {labels.status(receipt.status)}
          </Badge>
        </div>
        {receipt.description && (
          <div className="col-span-2">
            <p className="text-xs text-muted-foreground">{t("detail.labels.description")}</p>
            <p className="font-medium">{receipt.description}</p>
          </div>
        )}
        {receipt.remittance_information && (
          <div className="col-span-2">
            <p className="text-xs text-muted-foreground">{t("detail.labels.reference")}</p>
            <p className="font-medium">{receipt.remittance_information}</p>
          </div>
        )}
        {receipt.expense_category && (
          <div>
            <p className="text-xs text-muted-foreground">{t("detail.labels.category")}</p>
            <p className="font-medium capitalize">{receipt.expense_category}</p>
          </div>
        )}
        {receipt.payment_method && (
          <div>
            <p className="text-xs text-muted-foreground">{t("detail.labels.paymentMethod")}</p>
            <p className="font-medium capitalize">{labels.paymentMethod(receipt.payment_method)}</p>
          </div>
        )}
        {receipt.matched_transaction_id && (
          <div className="col-span-2">
            <p className="text-xs text-muted-foreground">{t("detail.labels.matchedTransaction")}</p>
            <p className="font-mono text-sm">{receipt.matched_transaction_id}</p>
          </div>
        )}
        {receipt.matched_at && (
          <div>
            <p className="text-xs text-muted-foreground">{t("detail.labels.matchedAt")}</p>
            <p className="font-medium">{formatDate(receipt.matched_at)}</p>
          </div>
        )}
        {receipt.status === "rejected" && receipt.rejected_at && (
          <div className="col-span-2">
            <p className="text-xs text-muted-foreground">{t("detail.labels.matchRemovedAt")}</p>
            <p className="font-medium">{formatDate(receipt.rejected_at)}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {t("detail.matchRemovedNote")}
            </p>
          </div>
        )}
        {verificationDisplay && (
          <div className="col-span-2">
            <p className="text-xs text-muted-foreground">{t("detail.labels.paymentVerification")}</p>
            <Badge
              variant="outline"
              className={
                receipt.paymentVerification?.status === "matched"
                  ? "bg-green-50 text-green-700 border-green-200"
                  : "bg-amber-50 text-amber-700 border-amber-200"
              }
            >
              {verificationDisplay.label}
            </Badge>
            {/* #498 Task 7 — design spec AC13: a later-arriving suggestion for an already
                acknowledged item must be visible on the detail view, not just the row. */}
            <LaterMatchFlag paymentVerification={receipt.paymentVerification} />
            <p className="text-xs text-muted-foreground mt-1">{verificationDisplay.description}</p>
          </div>
        )}
      </div>

      {/* #498 follow-up to whole-branch review finding I5: this detail dialog previously said
          "Match to Transaction" / "Re-match to Transaction" here while ReceiptsTable.tsx's row
          action for the identical unmatched/rejected case says "Mark as paid" — two different
          labels for the same underlying action on the same entity, contradicting the design
          spec's single-stable-label requirement (docs/superpowers/specs/
          2026-08-02-merge-paid-externally-match-action-design.md, lines 171/236; see
          ReceiptsTable.tsx's identical comment for the full rationale). Mirrored here verbatim. */}
      {(receipt.status === "unmatched" || receipt.status === "rejected") && (
        <Button
          className="w-full"
          onClick={() => {
            onOpenChange(false);
            onMatch(receipt);
          }}
        >
          {t("markAsPaid")}
        </Button>
      )}
      {/* #498 Task 8 — correction/reopen path (design spec "Correction path" UX Requirement,
          AC14, User Flow 7). Mirrors the "Edit" action added to ReceiptsTable's row — same
          reasoning: this detail view previously offered no way back into the match action once a
          receipt reached 'matched'. Reopens the SAME MarkAsMatchedModal via onMatch, not a
          separate "edit" mode.

          #498 Task 8 REVIEW FIX — deliberately excludes a GENUINE bank match (Finding 4 scoping
          decision — see ReceiptsTable.tsx's identical comment for the full rationale, incl. why
          `matched_transaction_id`, not `status`, is the signal that distinguishes a real match
          from the no-real-match paths for receipts specifically).

          #498 follow-up to finding I5: label changed from "Edit Match" to "Mark as paid" — same
          single-stable-label requirement as the primary action above, mirroring
          ReceiptsTable.tsx's reopen action (which already says "Mark as paid", not "Edit"). */}
      {receipt.status === "matched" && !receipt.matched_transaction_id && (
        <Button
          variant="outline"
          className="w-full"
          onClick={() => {
            onOpenChange(false);
            onMatch(receipt);
          }}
        >
          {t("markAsPaid")}
        </Button>
      )}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={
          receipt.file_path
            ? "max-w-5xl h-[90vh] flex flex-col"
            : "max-w-lg max-h-[90vh] overflow-y-auto"
        }
      >
        <DialogHeader>
          <DialogTitle>{receipt.file_name || receipt.vendor || t("detail.fallbackTitle")}</DialogTitle>
          <DialogDescription>
            {receipt.vendor || "—"} · {formatCurrency(receipt.amount, receipt.currency)}
          </DialogDescription>
        </DialogHeader>

        {receipt.file_path ? (
          <ResizablePanelGroup direction="horizontal" className="flex-1 mt-4">
            <ResizablePanel defaultSize={50} minSize={25}>
              <DocumentViewerPanel
                filePath={receipt.file_path}
                fileName={receipt.file_name ?? undefined}
              />
            </ResizablePanel>
            <ResizableHandle withHandle />
            <ResizablePanel defaultSize={50} minSize={30} className="pl-4">
              {detailPanel}
            </ResizablePanel>
          </ResizablePanelGroup>
        ) : (
          <div className="mt-4">{detailPanel}</div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ReceiptDetailDialog;